import { api } from '@core/api/apiClient';
import { captureApiError } from '@core/sentry';
import { currencyCode } from '@shared/utils/formatMoney';
import type { PaymentProcessor } from '../state/types';
import { CHARGING_PROCESSORS, GUARANTEE_REQUIRES, toAmount, toGuaranteeAdjustment, type GuaranteeAdjustment } from './guaranteeAdjustment';

/** The only row a pay link pays: a pending deposit increase (ReservationGuaranteeAdjustment's STATUS_PENDING, KIND_DEPOSIT_INCREASE). */
export const ADJUSTMENT_STATUS_PENDING = 'pending';
export const ADJUSTMENT_KIND_DEPOSIT_INCREASE = 'deposit_increase';
/** A block for a row the link no longer pays: not pending, or not a deposit increase. */
export const NO_LONGER_PAYABLE = 'no_longer_payable';
/** The Sentry source of the link's reads: the GET, and the fresh block a 409 quote_changed carries. */
export const LINK_SOURCE = 'guarantee-adjustment-link';

/** The emailed token: the backend's route only matches 64 hex characters. */
export const isAdjustmentToken = (token: string | undefined): token is string => /^[0-9a-f]{64}$/.test(token ?? '');

/** A staff-opened deposit difference, as the pay link shows it (`GuaranteeAdjustmentService::payLink()`). */
export interface AdjustmentLink {
  previousPartySize: number;
  newPartySize: number;
  /** The deposit before and after and what paying charges now, for the quote line; `outstanding.processor` null: nobody can charge it. */
  adjustment: GuaranteeAdjustment & { outstanding: NonNullable<GuaranteeAdjustment['outstanding']> };
  /** ISO 8601; null when the row has none. */
  expiresAt: string | null;
}

/** The booking's date and time, as the GET carries them. */
export interface AdjustmentLinkBooking {
  /** The serialized `resDate` / `resTime` DateTimes' `date` ("2026-11-02 00:00:00.000000"), for formatReservationDate/Time. */
  resDate: string | null;
  resTime: string | null;
}

/**
 * The link's block; NO_LONGER_PAYABLE for a row it no longer pays; null for a block this app cannot render or charge.
 * - A status or kind left out parses: the backend's 410 stays the guard.
 * - One that is not a string makes the block unreadable: the GET throws, and the app's query client reports it.
 * - A string other than pending / deposit_increase is refused as NO_LONGER_PAYABLE and reported here, once, with the
 *   field and its value: the backend answers 410 for a row it no longer pays, so a 200 or a 409 block carrying one is drift.
 */
export function toAdjustmentLink(raw: unknown): AdjustmentLink | typeof NO_LONGER_PAYABLE | null {
  if (!raw || typeof raw !== 'object') return null;
  const block = raw as Record<string, unknown>;
  const [status, kind] = [block.status ?? null, block.kind ?? null];
  if ((status !== null && typeof status !== 'string') || (kind !== null && typeof kind !== 'string')) return null;
  const drifted = status !== null && status !== ADJUSTMENT_STATUS_PENDING ? { field: 'status', value: status }
    : kind !== null && kind !== ADJUSTMENT_KIND_DEPOSIT_INCREASE ? { field: 'kind', value: kind } : null;
  if (drifted) {
    // Enum values, nothing private; the token in any URL on the event is scrubbed before it is sent.
    captureApiError(new Error(`guarantee-adjustment: a pay link with an unexpected ${drifted.field}`), { source: LINK_SOURCE, ...drifted });
    return NO_LONGER_PAYABLE;
  }
  const fees = toGuaranteeAdjustment({ ...block, requires: GUARANTEE_REQUIRES.PAYMENT, outstanding: null });
  const [amount, owedIn, surchargePercent] = [toAmount(block.outstanding), currencyCode(block.outstandingCurrency as string), toAmount(block.outstandingSurchargePercent)];
  const [previousPartySize, newPartySize] = [toAmount(block.previousPartySize), toAmount(block.newPartySize)];
  const processor = block.paymentProcessor;
  const charges = processor === null || (typeof processor === 'string' && CHARGING_PROCESSORS.has(processor));
  if (!fees || amount === null || owedIn === null || surchargePercent === null || previousPartySize === null || newPartySize === null || !charges) return null;
  return {
    previousPartySize,
    newPartySize,
    adjustment: { ...fees, outstanding: { amount, currency: owedIn, processor: processor as PaymentProcessor | null, surchargePercent } },
    expiresAt: typeof block.expiresAt === 'string' ? block.expiresAt : null,
  };
}

/** A serialized PHP DateTime's `date`. */
const dateOf = (value: unknown): string | null => {
  const date = (value as { date?: unknown } | null | undefined)?.date;
  return typeof date === 'string' ? date : null;
};

/**
 * GET /api/guarantee-adjustment/{token}. NO_LONGER_PAYABLE, or the ApiError of a 404/410 thrown: the link is no
 * longer valid. Throws an Error for a block this app cannot render. Authorized by the token alone: a stale session
 * header is never sent.
 */
export async function fetchAdjustmentLink(token: string): Promise<{ link: AdjustmentLink; booking: AdjustmentLinkBooking } | typeof NO_LONGER_PAYABLE> {
  const response = await api.get<{ adjustment?: unknown; reservation?: { resDate?: unknown; resTime?: unknown } }>(
    `/api/guarantee-adjustment/${token}`, { skipAuth: true },
  );
  const link = toAdjustmentLink(response?.adjustment);
  if (link === NO_LONGER_PAYABLE) return link;
  if (!link) throw new Error('guarantee-adjustment: a pay link this app cannot render');
  return { link, booking: { resDate: dateOf(response.reservation?.resDate), resTime: dateOf(response.reservation?.resTime) } };
}

/** What the pay POST carries: the outstanding the page showed, echoed as received, and on MercadoPago the card. */
export type AdjustmentPayment = { quotedOutstanding: number; tokenMP?: string; paymentMethod?: string; identityDocument?: string };

/**
 * POST /api/guarantee-adjustment/{token}/pay, which answers `{status: 'paid'}` or `{status: 'processing'}` (the hold was
 * taken and its capture is left to the reconcile). Anything but 'paid' is 'processing': never claim a payment not confirmed.
 */
export async function payAdjustmentLink(token: string, payment: AdjustmentPayment): Promise<'paid' | 'processing'> {
  const response = await api.post<{ status?: unknown }>(`/api/guarantee-adjustment/${token}/pay`, payment, { skipAuth: true });
  return response?.status === 'paid' ? 'paid' : 'processing';
}
